
 // src/lib/learning/lessonSounds.js

 const SOUND_FILES = {
  correct: "/sounds/correct.mpeg",
  levelComplete: "/sounds/level-complete.mpeg",
};

 let audioContext = null;
 let unlockInstalled = false;
 let audioPrimed = false;

 const soundBuffers = new Map();

 const TAG = "[Gwin Audio]";

 function log(stage, details = "") {
   console.info(`${TAG} ${stage}`, details);
 }

 function warn(stage, details = "") {
   console.warn(`${TAG} ${stage}`, details);
 }

 function reportError(stage, error) {
   console.error(`${TAG} ${stage}`, {
     name: error?.name || "UnknownError",
     message: error?.message || String(error),
   });
 }

 function getAudioContext() {
   if (typeof window === "undefined") return null;

   const AudioContextClass =
     window.AudioContext || window.webkitAudioContext;

   if (!AudioContextClass) {
     warn("WEB AUDIO UNSUPPORTED");
     return null;
   }

   if (!audioContext) {
     try {
       audioContext = new AudioContextClass();

       log("AUDIO CONTEXT CREATED", {
         state: audioContext.state,
         sampleRate: audioContext.sampleRate,
       });

       audioContext.addEventListener("statechange", () => {
         log("AUDIO CONTEXT STATE CHANGED", {
           state: audioContext.state,
         });
       });
     } catch (error) {
       reportError("AUDIO CONTEXT CREATION FAILED", error);
       return null;
     }
   }

   return audioContext;
 }

 function loadSound(name) {
   if (soundBuffers.has(name)) {
     return soundBuffers.get(name);
   }

   const url = SOUND_FILES[name];

   if (!url) {
     return Promise.reject(
       new Error(`Unknown sound: ${name}`)
     );
   }

   const promise = (async () => {
     log("FETCH STARTED", { name, url });

     // Avoid stale browser HTTP cache while diagnosing.
     const response = await fetch(url, {
       cache: "no-store",
     });

     const contentType =
       response.headers.get("content-type") || "";

     log("FETCH RESPONSE", {
       name,
       status: response.status,
       contentType,
       responseUrl: response.url,
     });

     if (!response.ok) {
       throw new Error(
         `Audio request failed: HTTP ${response.status}`
       );
     }

     if (
       contentType.includes("text/html") ||
       contentType.includes("application/json")
     ) {
       throw new Error(
         `Expected an audio file but received ${contentType}. Check the audio path and hosting configuration.`
       );
     }

     const data = await response.arrayBuffer();

     log("FILE LOADED", {
       name,
       bytes: data.byteLength,
     });

     if (data.byteLength === 0) {
       throw new Error("The audio file is empty.");
     }

     const context = getAudioContext();

     if (!context) {
       throw new Error("Web Audio API unavailable.");
     }

     log("DECODE STARTED", { name });

     const buffer = await context.decodeAudioData(data);

     log("DECODE SUCCESS", {
       name,
       durationSeconds: buffer.duration,
       channels: buffer.numberOfChannels,
       sampleRate: buffer.sampleRate,
     });

     return buffer;
   })().catch((error) => {
     soundBuffers.delete(name);
     reportError(`LOAD FAILED: ${name}`, error);
     throw error;
   });

   soundBuffers.set(name, promise);

   return promise;
 }

 function primeAudio(context) {
   if (audioPrimed) return;

   try {
     const buffer = context.createBuffer(
       1,
       1,
       context.sampleRate
     );

     const source = context.createBufferSource();
     source.buffer = buffer;
     source.connect(context.destination);
     source.start(0);

     audioPrimed = true;

     log("SILENT AUDIO PRIMED");
   } catch (error) {
     reportError("AUDIO PRIMING FAILED", error);
   }
 }

 function unlockAudio(trigger) {
   const context = getAudioContext();

   if (!context) {
     return Promise.resolve(false);
   }

   if (context.state === "running") {
     return Promise.resolve(true);
   }

   log("UNLOCK ATTEMPT", {
     trigger,
     stateBefore: context.state,
   });

   // Must be called during a genuine browser interaction.
   const resumePromise = context.resume();

   primeAudio(context);

   // Download sounds without triggering playback.
   Object.keys(SOUND_FILES).forEach((name) => {
     loadSound(name).catch(() => {});
   });

   return resumePromise
     .then(() => {
       const unlocked = context.state === "running";

       if (unlocked) {
         log("AUDIO UNLOCKED", { trigger });
       } else {
         warn("AUDIO STILL BLOCKED", {
           trigger,
           state: context.state,
         });
       }

       return unlocked;
     })
     .catch((error) => {
       reportError("AUDIO UNLOCK FAILED", error);
       return false;
     });
 }

 export function installLessonSoundUnlock() {
   if (
     unlockInstalled ||
     typeof window === "undefined"
   ) {
     return;
   }

   unlockInstalled = true;

   log("SOUND SYSTEM INSTALLED");

   const onGesture = (event) => {
     void unlockAudio(event.type);
   };

   window.addEventListener("pointerdown", onGesture, {
     capture: true,
     passive: true,
   });

   window.addEventListener("touchend", onGesture, {
     capture: true,
     passive: true,
   });

   window.addEventListener("click", onGesture, true);
   window.addEventListener("keydown", onGesture, true);

   // Temporary developer troubleshooting commands.
   window.gwinSoundDebug = {
     status() {
       const result = {
         installed: unlockInstalled,
         contextState: audioContext?.state || "not-created",
         audioPrimed,
         cachedSounds: Array.from(soundBuffers.keys()),
         files: SOUND_FILES,
         pageVisibility: document.visibilityState,
       };

       log("DIAGNOSTIC STATUS", result);
       return result;
     },

     async checkFiles() {
       const results = {};

       for (const name of Object.keys(SOUND_FILES)) {
         try {
           const buffer = await loadSound(name);

           results[name] = {
             loaded: true,
             duration: buffer.duration,
           };
         } catch (error) {
           results[name] = {
             loaded: false,
             error: error?.message || String(error),
           };
         }
       }

       log("FILE TEST RESULTS", results);
       return results;
     },

     testOnNextClick(name = "correct") {
       if (!SOUND_FILES[name]) {
         warn("INVALID TEST SOUND", { name });
         return;
       }

       log("TEST ARMED", {
         sound: name,
         instruction: "Click an empty area of the page.",
       });

       window.addEventListener(
         "click",
         (event) => {
           log("TEST CLICK RECEIVED", {
             isTrusted: event.isTrusted,
           });

           const unlockResult = unlockAudio(
             "diagnostic-click"
           );

           void unlockResult.then(() => {
             return playLessonSound(name);
           });
         },
         { capture: true, once: true }
       );
     },
   };

   log("DEBUG COMMANDS READY", {
     commands: [
       "window.gwinSoundDebug.status()",
       "window.gwinSoundDebug.checkFiles()",
       'window.gwinSoundDebug.testOnNextClick("correct")',
       'window.gwinSoundDebug.testOnNextClick("levelComplete")',
     ],
   });
 }

 export async function playLessonSound(name) {
   log("PLAY REQUESTED", {
     name,
     contextState: audioContext?.state || "not-created",
   });

   try {
     const context = getAudioContext();

     if (!context) {
       warn("PLAY FAILED: NO AUDIO CONTEXT", { name });
       return false;
     }

     const buffer = await loadSound(name);

     log("PLAYBACK CHECK", {
       name,
       contextState: context.state,
       durationSeconds: buffer.duration,
     });

     if (context.state !== "running") {
       warn("PLAY BLOCKED: AUDIO CONTEXT SUSPENDED", {
         name,
         state: context.state,
         suggestion:
           "The browser has not unlocked audio playback.",
       });

       return false;
     }

     const source = context.createBufferSource();
     const gain = context.createGain();

     source.buffer = buffer;
     source.loop = false;

     gain.gain.value =
       name === "correct" ? 0.7 : 0.8;

     source.connect(gain);
     gain.connect(context.destination);

     source.onended = () => {
       log("PLAYBACK ENDED", { name });
       source.disconnect();
       gain.disconnect();
     };

     source.start(0);

     log("PLAYBACK STARTED", {
       name,
       volume: gain.gain.value,
       contextState: context.state,
     });

     return true;
   } catch (error) {
     reportError(`PLAYBACK FAILED: ${name}`, error);
     return false;
   }
 }
